import { Check, AlertCircle } from '@untitledui/icons'
import React, { createContext, useCallback, useContext, useEffect, useRef, useState, type RefObject } from 'react'
import { createPortal } from 'react-dom'

import FadeTransition from '@components/FadeTransition'

interface FeedbackIconContextValue {
  indicateSuccess: () => void
  indicateError: () => void
}

const FeedbackIconContext = createContext<FeedbackIconContextValue | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components
export function useFeedbackIcon() {
  const ctx = useContext(FeedbackIconContext)
  if (!ctx) {
    throw new Error('useFeedbackIcon must be used within a FeedbackIconProvider')
  }
  return ctx
}

const VISIBLE_DURATION = 2500

type FeedbackKind = 'success' | 'error'

interface ActiveState {
  showing: boolean
  kind: FeedbackKind
}

interface FeedbackIconProviderProps {
  children: React.ReactNode

  // Optional container to portal the feedback icons into (otherwise rendered inline)
  portalContainer?: RefObject<HTMLDivElement | null>
}

const FeedbackIconProvider: React.FC<FeedbackIconProviderProps> = ({ children, portalContainer }) => {
  const [activeState, setActiveState] = useState<ActiveState>({ showing: false, kind: 'success' })
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  function clearTimers() {
    if (!hideTimerRef.current) return

    clearTimeout(hideTimerRef.current)
    hideTimerRef.current = null
  }

  const showFeedback = useCallback((kind: FeedbackKind) => {
    clearTimers()
    setActiveState({ showing: true, kind })
    hideTimerRef.current = setTimeout(() => {
      setActiveState(activeState => ({ ...activeState, showing: false }))
    }, VISIBLE_DURATION)
  }, [])

  const indicateSuccess = useCallback(() => showFeedback('success'), [showFeedback])
  const indicateError = useCallback(() => showFeedback('error'), [showFeedback])

  useEffect(() => () => clearTimers(), [])

  const feedbackIcon = (
    <FadeTransition
      show={activeState.showing}
      className="absolute top-1/2 left-1/2 z-50 -translate-1/2"
    >
      {activeState.kind === 'success' && <MyCheckCircle />}
      {activeState.kind === 'error' && <AlertCircle data-testid="feedback-error-icon" className="size-16 text-red-500" />}
    </FadeTransition>
  )

  return (
    <FeedbackIconContext.Provider value={{ indicateSuccess, indicateError }}>
      {children}
      {portalContainer?.current ? createPortal(feedbackIcon, portalContainer.current) : feedbackIcon}
    </FeedbackIconContext.Provider>
  )
}

function MyCheckCircle() {
  return (
    <div data-testid="feedback-success-icon" className={`
      flex size-16 items-center justify-center rounded-full bg-green-500 text-white
    `}>
      <Check className='stroke-3' size={34} />
    </div>
  )
}

export default FeedbackIconProvider
